import * as z from 'zod';

import {addToDataLayer} from '../../../utils/pageUtils';

import {IconOption, InvestableAssetsBucket} from './types';

export const emailSchema = z.email();

export const isEmailValid = (value: string): boolean =>
  emailSchema.safeParse(value).success;

// Classifies investable-assets amount into the lead-form bucket sent in the FHS payload.
export const getInvestableAssetsBucket = (
  amount: number,
): InvestableAssetsBucket => {
  if (amount < 100_000) return 'Under $100k';
  if (amount < 250_000) return '$100k-$250k';
  if (amount < 500_000) return '$250k-$500k';
  if (amount < 1_000_000) return '$500k-$1M';

  return '$1M+';
};

export const buildIconClassName = (
  opt: IconOption,
  isSelected: boolean,
): string => `${isSelected ? 'fa-solid' : opt.iconWeight} ${opt.iconName}`;

export const firstNameFrom = (
  fullName: string | undefined,
): string | undefined => fullName?.trim().split(/\s+/)[0];

// `overrideBase` bypasses the URL-path-derived prefix in addToDataLayer — the
// /financial-health-score URL isn't in the prefix table, so without override
// the event name would fall through to the default `FAAP_*`.
export const logFhsStepEntered = (action: string): void => {
  const values = {action};

  addToDataLayer({overrideBase: 'LM_form', values});
};

export const logFhsFormSubmitted = (): void => {
  const values = {lm_name: 'Financial Health Score'};

  addToDataLayer({overrideBase: 'lm_form_submitted', values});
};

// Computes the next multi-choice selection. Toggling the exclusive value
// (e.g. "None of these") collapses to [exclusive] or clears it; toggling any
// other value adds/removes it and always drops the exclusive.
export const computeNextValues = (
  selectedValues: string[],
  value: string,
  exclusive: string | undefined,
): string[] => {
  if (exclusive !== undefined && value === exclusive) {
    return selectedValues.includes(exclusive) ? [] : [exclusive];
  }

  if (selectedValues.includes(value)) {
    return selectedValues.filter((v) => v !== value);
  }

  return [...selectedValues.filter((v) => v !== exclusive), value];
};
